import React, { FC, useMemo } from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import { WithColor } from '@styles/colors';
import { NCFileVersion as NCFileVersionType } from '@interfaces/search';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import LeftColumn from '@components/ncFileVersions/components/LeftColumn';
import CompareCancelButton from '@components/ncFileVersions/components/CompareCancelButton';
import { useManageNCFileVersionPicker } from '@components/ncFileVersions/components/NCFileVersionPicker/hooks/useManageNCFileVersionPicker';
import { PICKER_FALLBACK_MAX_HEIGHT, PICKER_HEADER_FOOTER_RESERVE, PICKER_HORIZONTAL_PADDING, PICKER_MIN_LIST_HEIGHT } from '@components/ncFileVersions/components/NCFileVersionPicker/constants';
import { useNCFileVersionsWithPcsCounts } from '@components/ncFileVersions/hooks/useNCFileVersionsWithPcsCounts';

export interface NCFileVersionPickerProps extends WithColor {
  ncFileId?: number | null;
  versions: NCFileVersionType[];
  favoriteOrLatest: NCFileVersionType;
  initialSelection?: NCFileVersionType[];
  onCompare: (pair: NCFileVersionType[]) => void;
  onToggleFavoriteVersion?: (version: NCFileVersionType) => void;
  // Injected by the global Popup helper. hide closes the popup; maxHeight is the popup's available height (Popup always provides it — the fallback only applies when mounted directly, e.g. in tests).
  hide?: () => void;
  maxHeight?: number;
}

const NCFileVersionPicker: FC<NCFileVersionPickerProps> = ({ C, ncFileId, versions, favoriteOrLatest, initialSelection, onCompare, onToggleFavoriteVersion, hide, maxHeight }) => {
  const versionsWithPcs = useNCFileVersionsWithPcsCounts({ ncFileId, versions });
  const favoriteOrLatestWithPcs = useMemo(
    () => versionsWithPcs.find((version) => version.ncFileVersionId === favoriteOrLatest.ncFileVersionId) ?? favoriteOrLatest,
    [versionsWithPcs, favoriteOrLatest]
  );
  const initialSelectionWithPcs = useMemo(
    () => initialSelection?.map((selectedVersion) => versionsWithPcs.find((version) => version.ncFileVersionId === selectedVersion.ncFileVersionId) ?? selectedVersion),
    [initialSelection, versionsWithPcs]
  );

  const { favorite, otherVersions, versionsToCompare, toggleVersionForComparison, canCompare, handleCompare, handleCancel } = useManageNCFileVersionPicker({
    versions: versionsWithPcs,
    favoriteOrLatest: favoriteOrLatestWithPcs,
    initialSelection: initialSelectionWithPcs,
    onCompare,
    hide,
  });

  const listMaxHeight = Math.max(scale(PICKER_MIN_LIST_HEIGHT), (maxHeight ?? scale(PICKER_FALLBACK_MAX_HEIGHT)) - scale(PICKER_HEADER_FOOTER_RESERVE));

  return (
    <View style={{ paddingBottom: scale(15) }}>
      <Text style={[font.Headline, { color: C('Label/Light/Primary'), paddingHorizontal: scale(PICKER_HORIZONTAL_PADDING), paddingTop: scale(18), paddingBottom: scale(12) }]}>
        {i18n.t('Select another version to compare')}
      </Text>

      <View style={{ paddingHorizontal: scale(PICKER_HORIZONTAL_PADDING) }}>
        <LeftColumn
          C={C}
          favoriteOrLatest={favoriteOrLatestWithPcs}
          favorite={favorite}
          selectedVersion={favoriteOrLatestWithPcs}
          otherVersions={otherVersions}
          showOnlyActiveVersions
          comparisonMode
          versionsToCompare={versionsToCompare}
          onToggleVersionForComparison={toggleVersionForComparison}
          onToggleFavoriteVersion={onToggleFavoriteVersion}
          scrollStyle={{ maxHeight: listMaxHeight }}
        />
      </View>

      <View testID={TEST_IDS.ncFileVersions.versionPicker} style={{ paddingHorizontal: scale(PICKER_HORIZONTAL_PADDING), paddingTop: scale(12), gap: scale(10) }}>
        <CompareCancelButton testID={TEST_IDS.ncFileVersions.compareStartButton} C={C} variant="primary" disabled={!canCompare} onPress={handleCompare} />
        <CompareCancelButton testID={TEST_IDS.ncFileVersions.compareModeCancelButton} C={C} variant="secondary" onPress={handleCancel} />
      </View>
    </View>
  );
};

export default NCFileVersionPicker;
